import { timeout } from '../timeoutUtils';
import { AppState } from 'stores/appState';
import { history } from 'nomst/Stores/Navigation/router.store';
import httpService from 'stores/http-service';
import { DataState, httpResponseConstants, TeamRoutes } from 'typings/enums';
import { IDataManager, GetDataOptions } from 'typings/dataManagers';

export interface IDataStateAware {
    setDataState(state: DataState): void;
}

interface DataManagerProps {
    appState: AppState;
}

class DataManager implements IDataManager {

    public readonly maxCacheEntries: number = 100;

    _appState: AppState;

    constructor(props: DataManagerProps) {
        this._appState = props.appState;
    }

    _buildUrl(route: string, baseUrl: Nullable<string> = null): string {
        return baseUrl ? `${baseUrl}/${route}` : `${route}`;
    }

    getData<t>(
        getDataFunc: Function,
        blockUi: boolean = true
    ): Promise<Nullable<t>> {
        const options: GetDataOptions = {
            getDataFunc,
            blockUi
        };
        return this.getDataWithOptions(options);
    }

    async getDataWithOptions<t>(
        dataOptions: GetDataOptions
    ): Promise<Nullable<t>> {
        try {
            if (dataOptions.blockUi) {
                // trigger ui overlay to be active
                this.transitionDataState(DataState.Pending);
            }

            // then request data
            const data = await timeout(dataOptions.getDataFunc()) as t;

            // then set state to idle if suceeded
            if (dataOptions.blockUi) {
                this.transitionDataState(DataState.Idle);
            }

            return data;
        } catch (error) {
            // Team uses forbidden to redirect to the Access Denied page
            if (dataOptions.ignoreFailure || error === httpResponseConstants.Forbidden) {
                return null;
            }
            if (dataOptions.redirectOnError) {
                history.push(TeamRoutes.Error);
            }
            // Determine if we timedout or not
            this.transitionDataState(
                error.message === 'timeout' ? DataState.Timeout : DataState.Error);
            return null;
        }
    }

    // Does random cache eviction if number of items in object
    // is >= cache limit
    _enforceCacheLimit(obj: {[id: string]: {}}): void {
        if (typeof obj !== 'object') {
            return;
        }

        const keys = Object.keys(obj);
        if (keys.length >= this.maxCacheEntries) {
            const index = Math.floor(Math.random() * keys.length);
            delete obj[keys[index]];
        }
    }

    async _getDataRequest<T>(
        route: string,
        redirectOnFail: boolean = true,
        includeCredentials: boolean = false
    ): Promise<T|null> {
        return await httpService.get<T>(this._buildUrl(route), redirectOnFail, includeCredentials);
    }

    async _getSalesForceApi<T>(route: string): Promise<T|null> {
        return await httpService.getSimple<T>(route);
    }

    async _postDataRequest<T, X>(route: string, data: T): Promise<X|null> {
        return await httpService.post<T, X>(this._buildUrl(route), data);
    }
    async _deleteWithRequestBody<T, X>(route: string, data: T): Promise<X|null> {
        return await httpService.deleteWithRequestBody<T, X>(this._buildUrl(route), data);
    }

    async _putDataRequest<T, X>(route: string, data: T): Promise<X|null> {
        return await httpService.put<T, X>(this._buildUrl(route), data);
    }

    async _deleteDataRequest<T>(route: string, baseUrl: Nullable<string> = null): Promise<T|null> {
        return await httpService.delete<T>(this._buildUrl(route, baseUrl));
    }

    async _downloadDataRequest<T>(url: string, csvFilename: string, type: 'GET' | 'POST', data?: T): Promise<void> {
        const blobUrl = await httpService.download(url, type, data);
        return httpService.displaySaveFileDialog(blobUrl, csvFilename);
    }

    _postAsForm<X>(url: string, data: FormData): Promise<X|null> {
        return httpService.postAsForm<X>(this._buildUrl(url), data);
    }

    // making public for testing purposes
    transitionDataState(state: DataState): void {
        this.protectedTransitionDataState(state);
    }

    protected protectedTransitionDataState(state: DataState): void {
        this._appState.transitionDataState(state);
    }
}

export { DataManager };
export default DataManager;